import React, { useEffect, useState } from 'react'
import { Box, Button, ButtonGroup, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import PageSection from '../../Constants&Theme/PageSection'
import { stackGap } from '../../Constants&Theme/Spacing'
import { Undo, VolumeDown, VolumeMute, VolumeOff, VolumeUp } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { green, red } from '@mui/material/colors'
import QRCode from 'react-qr-code'
import ValidateTime from './Cards/ValidateTime'
import { format } from 'date-fns'

function ReaderSettings() {
  const navigate = useNavigate()
  const [volume, setVolume] = useState(1)
  const [time, setTime] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [time])

  return (
    <Box
      maxWidth={'md'}
      width={1}
    >
      <PageSection
        title={'Code Reader Settings'}
        leftDecorator={
          <Button
            variant={'outlined'}
            onClick={() => navigate('/stuart/reader')}
            startIcon={<Undo />}
            sx={{ ml: 'auto' }}
          >
            Back to Reader
          </Button>
        }
      >
        <Stack
          width={1}
          gap={stackGap.col}
          sx={{ mb: 2 }}
        >
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Recommended Communication Mode
                  </Typography>
                  <Typography variant="h5">Bluetooth HID mode</Typography>
                  <Typography variant="body2">
                    The Bluetooth matched previously will be connected
                    automatically when not plugged in.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: 14,
                      mt: 1,
                      mb: 1 / 3,
                    }}
                  >
                    Connection indicator on reader screen
                  </Typography>
                  <Stack
                    direction={'row'}
                    gap={stackGap.row}
                  >
                    <Chip
                      label={'Connected by cable'}
                      icon={<Typography color={green['A700']}>USB</Typography>}
                    />
                    <Chip
                      label={'No Connection'}
                      icon={<Typography color={red['A700']}>HIDx</Typography>}
                    />
                    <Chip
                      label={'Connected by Bluetooth'}
                      icon={<Typography color={green['A700']}>HID</Typography>}
                    />
                  </Stack>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={'%%SpecCodeAA'}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Recommended Speed Mode
                  </Typography>
                  <Typography variant="h5">
                    Fast Bluetooth Upload Speed
                  </Typography>
                  <Typography variant="body2">
                    Maximium speed of upload to computer
                  </Typography>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={'%%SpecCodeB0'}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Recommended Scan Mode
                  </Typography>
                  <Typography variant="h5">Manual Scan Mode</Typography>
                  <Typography variant="body2">
                    Requires user to click and hold <b>SCAN</b> button to scan.
                  </Typography>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={
                    '$>:BATCHST.<$$>:S01010F.<$;$>:SFF0200.<$;$>:S01000F.<$;$>:BATCHET.<$;'
                  }
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Recommended Standby Time
                  </Typography>
                  <Typography variant="h5">Standby: 10 min</Typography>
                  <Typography variant="body2">
                    The Sleep Time Setting best optimised for minimising
                    interruption while maximising battery life.
                  </Typography>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={'%%SpecCode34'}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Correct System Date & Time is important!
                  </Typography>
                  <Typography variant="h5">Update System Clock</Typography>
                  <Typography variant="body2">
                    {`Scan code to set device clock to ${format(time, 'yyyy/MM/dd h:mm:ssa')}`}
                  </Typography>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={'%%SpecCode88' + format(time, 'yyyyMMddHHmmss')}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Required Time Display
                  </Typography>
                  <Typography variant="h5">Display Time at the Back</Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1 }}
                  >
                    Appends the timestamp to the back of the code.
                  </Typography>
                  <ValidateTime />
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={'%%SpecCodeC2'}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
          <Card
            variant="outlined"
            sx={{ flex: 1 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: 14 }}
                    gutterBottom
                  >
                    Optional Sound Setting
                  </Typography>
                  <Typography variant="h5">Sound Volume</Typography>
                  <Typography variant="body2">Sets the volume level</Typography>
                  <ButtonGroup sx={{ mt: 1 / 2 }}>
                    <Button
                      onClick={() => setVolume(0)}
                      disabled={volume === 0}
                      variant={'outlined'}
                      size={'small'}
                      startIcon={<VolumeOff />}
                    >
                      Off
                    </Button>
                    <Button
                      onClick={() => setVolume(1)}
                      disabled={volume === 1}
                      variant={'outlined'}
                      size={'small'}
                      startIcon={<VolumeMute />}
                    >
                      Low
                    </Button>
                    <Button
                      onClick={() => setVolume(2)}
                      disabled={volume === 2}
                      variant={'outlined'}
                      size={'small'}
                      startIcon={<VolumeDown />}
                    >
                      Medium
                    </Button>
                    <Button
                      onClick={() => setVolume(3)}
                      disabled={volume === 3}
                      variant={'outlined'}
                      size={'small'}
                      startIcon={<VolumeUp />}
                    >
                      High
                    </Button>
                  </ButtonGroup>
                </Box>
                <QRCode
                  size={100}
                  style={{ height: '100px', width: '100px' }}
                  value={`%%SpecCode9${4 + volume}`}
                  viewBox={`0 0 256 256`}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </PageSection>
    </Box>
  )
}

export default ReaderSettings
